import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  AddToCart,
  ClearCart,
  DeleteCartItem,
  GetCart,
  SetCart,
  SetCartAddress,
  SetCartSelectedClient,
  SetCartTaxRate,
  SetOverlappingStatus,
} from './cart.actions';
import { DiscountTypes } from '../../schema/pos/cart';
import { Room } from 'glamera-shared';
import { PaymentMethodType } from 'src/app/models/enums/payment-method-type';

export interface CartStateModel {
  clientAddress: AddressStateModel;
  selectedClient: ClientStateModel;
  hasWallet: boolean;
  walletValue: number;
  hasGiftCard: boolean;
  giftCardValue: number;
  giftCardCode: string;
  loyaltyDiscountValue: number;
  hasLoyaltyPoints: boolean;
  loyalityRedeemWay: LoyalityRedeemWay;
  loyaltyPredefinedId?: number;
  loyaltyNumOfPoints: number;
  hasPromoCode: boolean;
  hasCash: boolean;
  cashValue: number;
  id: number;
  branch: BranchStateModel;
  currencyCode: string;
  promoCode?: any;

  subTotalPrice: number;
  promoDiscountValue: number;
  discountType: DiscountTypes | null;
  discountValue: number;
  discountPercent: number;
  subTotalPriceAfterDiscount: number;

  taxRate: number;
  totalTax: number;
  totalPriceWithTax: number;

  itemsCount: number;
  minDepositValue: number;
  minDepositPercent: number;
  isMinDepositRequired: boolean;
  remainingToPay: number;
  items: CartItemStateModel[];
  isOverlapping: boolean;
  paymentMethods?: PaymentMethodStateModel[];
}

export interface PaymentMethodStateModel {
  name: string;
  value: number;
  paymentMethodType: PaymentMethodType;
  paymentMethodId: number;
}

export interface ClientStateModel {
  id: number;
  imagePath: string;
  isBlackList: boolean;
  isBlocked: boolean;
  mobile: string;
  name: string;
  visitsCount: number;
}

export interface AddressStateModel {
  location: { lat: number; lng: number };
  id: number;
  isSelected: boolean;
  label: string;
  area: string;
  street: string;
  building: string;
  appartment: string;
}

export interface BranchStateModel {
  id: number;
  name: string;
  acceptPaymentOnline: number;
}

export enum LoyalityRedeemWay {
  Predefined = 1,
  ExchangeRate = 2,
}
export interface EmployeeStateModel {
  id: number;
  name: string;
}

export interface AppointmentStateModel {
  room?: Room;
  date: string;
  startTime: string;
  endTime: string;
  itemPlace: number;
  duration?: number;
  employees: EmployeeStateModel[];
  clientAddress?: any;
  discount?: number;
  discountType?: number;
}

export interface CartItemStateModel {
  id: number;
  itemId: number;
  itemType: number;
  itemTypeDesc: string;
  itemCategory?: string;
  name: string;
  quantity: number;
  unitId?: number;

  discountType?: DiscountTypes;
  discountValue?: number;
  discountPercent?: number;

  price: number;

  taxValue: number;
  priceWithTax: number;

  totalPrice: number;

  totalTaxValue: number;
  totalPriceWithTax: number;

  currencyCode: string;
  image?: string;
  expirationDate?: any;
  isForMe?: boolean;
  isMultiTimeSlot?: boolean;
  allowMultipleEmployees?: boolean;
  appointment?: AppointmentStateModel;
  children?: CartItemChildStateModel[];
  recipient?: {
    phoneNumber?: string;
    countryCode?: string;
  };
}

export interface CartItemChildStateModel {
  id: number;
  itemId: number;
  itemType: number;
  itemTypeDesc?: string;
  itemCategory?: string;
  name: string;
  allowMultipleEmployees?: boolean;
  appointment?: AppointmentStateModel;
}

@Injectable()
@State<CartStateModel>({
  name: 'cart',
  defaults: {
    selectedClient: {
      id: 0,
      imagePath: '',
      isBlackList: false,
      isBlocked: false,
      mobile: '',
      name: '',
      visitsCount: 0,
    },
    clientAddress: {
      location: { lat: 0, lng: 0 },
      id: 0,
      isSelected: false,
      label: '',
      area: '',
      street: '',
      building: '',
      appartment: '',
    },
    hasWallet: false,
    walletValue: 0,
    hasGiftCard: false,
    giftCardValue: 0,
    giftCardCode: '',
    loyaltyDiscountValue: 0,
    hasLoyaltyPoints: false,
    loyalityRedeemWay: LoyalityRedeemWay.Predefined,
    loyaltyPredefinedId: 0,
    loyaltyNumOfPoints: 0,
    hasPromoCode: false,
    hasCash: false,
    cashValue: 0,
    paymentMethods: [],
    id: 0,
    branch: {
      id: 0,
      name: '',
      acceptPaymentOnline: 0,
    },
    currencyCode: '',
    promoCode: null,
    subTotalPrice: 0,
    promoDiscountValue: 0,
    discountType: null,
    discountValue: 0,
    discountPercent: 0,
    subTotalPriceAfterDiscount: 0,
    taxRate: 0,
    totalTax: 0,
    totalPriceWithTax: 0,
    itemsCount: 0,
    minDepositValue: 0,
    minDepositPercent: 0,
    isMinDepositRequired: false,
    remainingToPay: 0,
    items: [],
    isOverlapping: false,
  },
})
export class CartState {
  constructor() {} // private userCartService: UserCartService

  @Selector()
  static isOverlapping(state: CartStateModel) {
    return state.isOverlapping;
  }

  @Selector()
  static cart(state: CartStateModel) {
    return state;
  }

  @Action(GetCart)
  getCart(ctx: StateContext<CartStateModel>, action: GetCart) {
    // return this.userCartService.getCart().pipe(
    //   tap((result) => {
    //     ctx.patchState(result.data);
    //   })
    // );
  }

  @Action(AddToCart)
  addToCart(ctx: StateContext<CartStateModel>, action: AddToCart) {
    let cart = ctx.getState();
    let exist = cart.items.find((el) => el.itemId === action.payload.itemId);
    if (!exist) cart.items.push(action.payload);
    this.updateCartCalculations(cart);
    // ctx.setState(cart);
    ctx.patchState(cart);
  }

  private updateCartCalculations(cart: CartStateModel) {
    cart.subTotalPrice = cart.items
      .map((item) => item.totalPrice)
      .reduce((acc, curr) => acc + curr, 0);

    cart.discountValue = cart.items
      .map((item) => item.discountValue ?? 0)
      .reduce((acc, curr) => acc + curr, 0);

    cart.discountPercent = (cart.discountValue / cart.subTotalPrice) * 100;

    cart.subTotalPriceAfterDiscount = cart.subTotalPrice - cart.discountValue;

    cart.totalTax = cart.items
      .map((item) => item.totalTaxValue)
      .reduce((acc, curr) => acc + curr, 0);

    cart.totalPriceWithTax = cart.subTotalPriceAfterDiscount + cart.totalTax;

    cart.itemsCount = cart.items.length;
  }

  @Action(DeleteCartItem)
  deleteCartItem(ctx: StateContext<CartStateModel>, action: DeleteCartItem) {
    let cart = ctx.getState();
    cart.items = cart.items.filter((el) => el.itemId !== action.payload);
    this.updateCartCalculations(cart);

    ctx.patchState(cart);
  }

  @Action(ClearCart)
  clearCart(ctx: StateContext<CartStateModel>, action: ClearCart) {
    // return this.userCartService.clearCart().pipe(
    //   tap((result) => {
    //     ctx.patchState(result.data);
    //   })
    // );
    ctx.patchState({
      selectedClient: {
        id: 0,
        imagePath: '',
        isBlackList: false,
        isBlocked: false,
        mobile: '',
        name: '',
        visitsCount: 0,
      },
      clientAddress: {
        location: { lat: 0, lng: 0 },
        id: 0,
        isSelected: false,
        label: '',
        area: '',
        street: '',
        building: '',
        appartment: '',
      },
      hasWallet: false,
      walletValue: 0,
      hasGiftCard: false,
      giftCardValue: 0,
      giftCardCode: '',
      loyaltyDiscountValue: 0,
      hasLoyaltyPoints: false,
      loyalityRedeemWay: LoyalityRedeemWay.Predefined,
      loyaltyPredefinedId: 0,
      loyaltyNumOfPoints: 0,
      hasPromoCode: false,
      hasCash: false,
      cashValue: 0,
      paymentMethods: [],
      id: 0,
      branch: {
        id: 0,
        name: '',
        acceptPaymentOnline: 0,
      },
      currencyCode: '',
      promoCode: null,
      subTotalPrice: 0,
      promoDiscountValue: 0,
      discountType: null,
      discountValue: 0,
      discountPercent: 0,
      subTotalPriceAfterDiscount: 0,
      taxRate: 0,
      totalTax: 0,
      totalPriceWithTax: 0,
      itemsCount: 0,
      minDepositValue: 0,
      minDepositPercent: 0,
      isMinDepositRequired: false,
      remainingToPay: 0,
      items: [],
      isOverlapping: false,
    });
  }

  @Action(SetCart)
  setCart(ctx: StateContext<CartStateModel>, action: SetCart) {
    ctx.patchState(action.payload);
  }

  @Action(SetOverlappingStatus)
  setOverlappingStatus(
    ctx: StateContext<CartStateModel>,
    action: SetOverlappingStatus
  ) {
    ctx.patchState({ isOverlapping: action.payload });
  }

  @Action(SetCartAddress)
  setCartAddress(ctx: StateContext<CartStateModel>, action: SetCartAddress) {
    // return this.userCartService.selectAddress(action.payload).pipe(
    //   tap((result) => {
    //     ctx.patchState(result.data);
    //   })
    // );
  }

  @Action(SetCartTaxRate)
  setCartTaxRate(ctx: StateContext<CartStateModel>, action: SetCartTaxRate) {
    ctx.patchState({
      taxRate: action.payload,
    });
  }

  @Action(SetCartSelectedClient)
  setCartSelectedClient(
    ctx: StateContext<CartStateModel>,
    action: SetCartSelectedClient
  ) {
    ctx.patchState({
      selectedClient: action.payload,
    });
  }
}
